// frontend/src/components/dialogs/SegmentMatrixModal.jsx
// Modal for managing route segment travel times

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Calculator, Clock, MapPin, AlertTriangle, Check, Edit2, Save, Route, Trash2, Plus, Loader2 } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { ThemedButton } from '../themed';
import ConfirmDialogShell from './ConfirmDialogShell';
import segmentMatrixService from '../../services/segmentMatrixService';
import { isValidVariantName, sanitizeVariantName } from '../../utils/variantNaming';
import { buildSegmentKey, formatDurationMinutes, isSequentialToStateToken } from '../../utils/segmentUtils';
import { useDateTimePreferences } from '../../hooks/useDateTimePreferences';
import {
    displayDistanceToMeters as displayDistanceValueToMeters,
    formatDistanceKilometers,
    getDistanceUnitLabel,
    getZeroDistanceLabel,
    kilometersToDisplayDistance,
    normalizeDistanceUnit
} from '../../utils/distanceUtils';

const SegmentMatrixModal = ({
    isOpen,
    onClose,
    entityId,
    routeId,
    routeName,
    waypoints = []
}) => {
    const { t } = useTranslation();
    const { background, text, border, cn } = useTheme();
    const { distanceUnit, formatDateTime } = useDateTimePreferences();
    const resolvedDistanceUnit = normalizeDistanceUnit(distanceUnit);
    const distanceUnitLabel = getDistanceUnitLabel(resolvedDistanceUnit, t);
    const zeroDistanceLabel = getZeroDistanceLabel(resolvedDistanceUnit, t);
    const translatedWaypointMap = useMemo(() => (
        new Map(
            waypoints.map((waypoint) => [
                String(waypoint.id),
                {
                    name: waypoint.displayName || waypoint.name,
                    address: waypoint.displayAddress || waypoint.address
                }
            ])
        )
    ), [waypoints]);

    const getWaypointName = useCallback((waypoint) => {
        const translatedWaypoint = translatedWaypointMap.get(String(waypoint?.id));
        return translatedWaypoint?.name || waypoint?.displayName || waypoint?.name || '';
    }, [translatedWaypointMap]);

    const getSegmentLabel = useCallback((fromWaypoint, toWaypoint) => (
        `${getWaypointName(fromWaypoint)} → ${getWaypointName(toWaypoint)}`
    ), [getWaypointName]);

    // State
    const [status, setStatus] = useState(null);
    const [segments, setSegments] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isCalculating, setIsCalculating] = useState(false);
    const [error, setError] = useState(null);
    const [editingSegment, setEditingSegment] = useState(null);
    const [editValue, setEditValue] = useState('');
    const [editDistanceValue, setEditDistanceValue] = useState('');
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [updatingPolicySegmentKey, setUpdatingPolicySegmentKey] = useState(null);
    const [updatingDetectionModeSegmentKey, setUpdatingDetectionModeSegmentKey] = useState(null);
    const [updatingAttributesSegmentKey, setUpdatingAttributesSegmentKey] = useState(null);

    // State for adding new segment
    const [showAddSegment, setShowAddSegment] = useState(false);
    const [newFromWaypointId, setNewFromWaypointId] = useState('');
    const [newToWaypointId, setNewToWaypointId] = useState('');
    const [isAddingSegment, setIsAddingSegment] = useState(false);

    // State for deleting segment
    const [deletingSegment, setDeletingSegment] = useState(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    // State for saving ORS reference (per-row shortcut segments)
    const [savingORSSegmentKey, setSavingORSSegmentKey] = useState(null);
    const [orsMessage, setOrsMessage] = useState(null); // { type: 'success'|'skip'|'error', text, key }
    const [orsConfirmSegment, setOrsConfirmSegment] = useState(null);
    const [showORSConfirm, setShowORSConfirm] = useState(false);

    // State for ORS alternatives selection
    const [orsAlternatives, setOrsAlternatives] = useState(null); // { segment, alternatives: [...] }
    const [selectedAlternativeIndex, setSelectedAlternativeIndex] = useState(0);
    const [alternativeVariantName, setAlternativeVariantName] = useState('');
    const [fetchingAlternatives, setFetchingAlternatives] = useState(null); // segment key while fetching
    const [savingAlternative, setSavingAlternative] = useState(false);

    const distanceKmToDisplayValue = useCallback((distanceKm) => {
        if (distanceKm === null || distanceKm === undefined || distanceKm === '') return null;
        return kilometersToDisplayDistance(distanceKm, resolvedDistanceUnit);
    }, [resolvedDistanceUnit]);

    const formatSegmentDistanceForDisplay = useCallback((distanceKm) => {
        return formatDistanceKilometers(distanceKm, resolvedDistanceUnit, t, {
            compact: false,
            zeroLabel: zeroDistanceLabel
        });
    }, [resolvedDistanceUnit, t, zeroDistanceLabel]);

    const displayDistanceInputToMeters = useCallback((distanceValue) => {
        return displayDistanceValueToMeters(distanceValue, resolvedDistanceUnit);
    }, [resolvedDistanceUnit]);

    // State for saving ORS sequential reference (section-level button)
    const [savingORSSequential, setSavingORSSequential] = useState(false);
    const [orsSequentialMessage, setOrsSequentialMessage] = useState(null); // { type, text }
    const [showORSSequentialConfirm, setShowORSSequentialConfirm] = useState(false);
    const [orsRoutingPreference, setOrsRoutingPreference] = useState('recommended');

    // Load status and segments when modal opens
    const loadData = useCallback(async () => {
        if (!isOpen || !entityId || !routeId) return;

        setIsLoading(true);
        setError(null);

        try {
            const [statusResult, segmentsResult] = await Promise.all([
                segmentMatrixService.getTravelTimesStatus(entityId, routeId),
                segmentMatrixService.getSegmentDetails(entityId, routeId)
            ]);

            setStatus(statusResult.data);
            setSegments((segmentsResult.data || []).map(segment => ({
                ...segment,
                archivedType: segment.archived_type || 'representative',
                variant: segment.variant ?? null,
                providerPolicy: segment.providerPolicy || segment.provider_policy || 'provider_allowed',
                waypointDetectionMode: segment.waypointDetectionMode ?? segment.waypoint_detection_mode ?? 'auto',
                segmentAttributes: segment.segmentAttributes || segment.segment_attributes || 'simple'
            })));
        } catch (err) {
            console.error('Error loading segment data:', err);
            setError(err.response?.data?.error || t('errorLoadingSegments'));
        } finally {
            setIsLoading(false);
        }
    }, [isOpen, entityId, routeId, t]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    // Show confirmation dialog before calculating
    const handleCalculateClick = () => {
        setShowConfirmDialog(true);
    };

    // Handle calculate travel times (called after confirmation)
    const handleCalculate = async () => {
        setShowConfirmDialog(false);
        setIsCalculating(true);
        setError(null);

        try {
            const result = await segmentMatrixService.calculateTravelTimes(entityId, routeId, {
                includeShortcuts: true // Include all shortcut combinations for skippable stops
            });

            if (result.success) {
                await loadData(); // Reload data after successful calculation
            } else {
                setError(result.data?.errors?.[0]?.error || t('calculationPartiallyFailed'));
            }
        } catch (err) {
            console.error('Error calculating travel times:', err);
            setError(err.response?.data?.error || t('calculationFailed'));
        } finally {
            setIsCalculating(false);
        }
    };

    // Handle manual segment edit
    const handleSaveEdit = async (segment) => {
        const durationMinutes = parseFloat(editValue);
        if (isNaN(durationMinutes) || durationMinutes <= 0) {
            return;
        }
        const hasDistanceValue = editDistanceValue !== null
            && editDistanceValue !== undefined
            && editDistanceValue.trim() !== '';
        const distanceMeters = hasDistanceValue ? displayDistanceInputToMeters(editDistanceValue) : null;
        if (hasDistanceValue && (!Number.isFinite(distanceMeters) || distanceMeters < 0)) {
            return;
        }

        try {
            await segmentMatrixService.updateSegment(
                entityId,
                routeId,
                segment.fromWaypoint.id,
                segment.toWaypoint.id,
                durationMinutes,
                distanceMeters,
                segment.archivedType || 'representative',
                segment.variant ?? null,
                segment.isSequential
            );
            setEditingSegment(null);
            setEditValue('');
            setEditDistanceValue('');
            await loadData();
        } catch (err) {
            console.error('Error updating segment:', err);
            setError(err.response?.data?.error || t('updateFailed'));
        }
    };

    const getSegmentActionKey = (segment) => (
        `${buildSegmentKey(segment.fromWaypoint.id, segment.toWaypoint.id)}-${segment.archivedType || 'representative'}-${segment.variant || 'standard'}-${isSequentialToStateToken(segment.isSequential)}`
    );

    const isPrivateRouteSegment = (segment) => (
        (segment?.providerPolicy || segment?.provider_policy) === 'private_route'
    );

    const handleProviderPolicyToggle = async (segment, checked) => {
        const providerPolicy = checked ? 'private_route' : 'provider_allowed';
        const segmentKey = getSegmentActionKey(segment);
        setUpdatingPolicySegmentKey(segmentKey);
        setError(null);

        try {
            await segmentMatrixService.updateProviderPolicy(
                entityId,
                routeId,
                segment.fromWaypoint.id,
                segment.toWaypoint.id,
                providerPolicy,
                segment.archivedType || 'representative',
                segment.variant ?? null,
                segment.isSequential
            );
            setSegments(prev => prev.map(s =>
                getSegmentActionKey(s) === segmentKey
                    ? { ...s, providerPolicy }
                    : s
            ));
        } catch (err) {
            console.error('Error updating segment provider policy:', err);
            setError(err.response?.data?.error || t('privateRouteUpdateFailed'));
        } finally {
            setUpdatingPolicySegmentKey(null);
        }
    };

    const handleWaypointDetectionModeChange = async (segment, value) => {
        const waypointDetectionMode = value;
        const segmentKey = getSegmentActionKey(segment);
        setUpdatingDetectionModeSegmentKey(segmentKey);
        setError(null);

        try {
            await segmentMatrixService.updateWaypointDetectionMode(
                entityId,
                routeId,
                segment.fromWaypoint.id,
                segment.toWaypoint.id,
                waypointDetectionMode,
                segment.archivedType || 'representative',
                segment.variant ?? null,
                segment.isSequential
            );
            setSegments(prev => prev.map(s =>
                getSegmentActionKey(s) === segmentKey
                    ? { ...s, waypointDetectionMode }
                    : s
            ));
        } catch (err) {
            console.error('Error updating waypoint detection mode:', err);
            setError(err.response?.data?.error || t('waypointDetectionModeUpdateFailed'));
        } finally {
            setUpdatingDetectionModeSegmentKey(null);
        }
    };

    const handleSegmentAttributesChange = async (segment, value) => {
        const segmentAttributes = value;
        const segmentKey = getSegmentActionKey(segment);
        setUpdatingAttributesSegmentKey(segmentKey);
        setError(null);

        try {
            await segmentMatrixService.updateSegmentAttributes(
                entityId,
                routeId,
                segment.fromWaypoint.id,
                segment.toWaypoint.id,
                segmentAttributes,
                segment.archivedType || 'representative',
                segment.variant ?? null,
                segment.isSequential
            );
            setSegments(prev => prev.map(s =>
                getSegmentActionKey(s) === segmentKey
                    ? { ...s, segmentAttributes }
                    : s
            ));
        } catch (err) {
            console.error('Error updating segment attributes:', err);
            setError(err.response?.data?.error || t('segmentAttributesUpdateFailed', 'Failed to update segment attributes'));
        } finally {
            setUpdatingAttributesSegmentKey(null);
        }
    };

    // Handle add new segment
    const handleAddSegment = async () => {
        if (!newFromWaypointId || !newToWaypointId) {
            setError(t('selectBothWaypoints'));
            return;
        }

        if (newFromWaypointId === newToWaypointId) {
            setError(t('cannotSelectSameWaypoint'));
            return;
        }

        // Check if segment already exists
        const existingSegment = segments.find(
            s => s.fromWaypoint.id === parseInt(newFromWaypointId) &&
                 s.toWaypoint.id === parseInt(newToWaypointId)
        );

        if (existingSegment) {
            setError(t('segmentAlreadyExists'));
            return;
        }

        setIsAddingSegment(true);
        setError(null);

        try {
            await segmentMatrixService.calculateSingleSegment(
                entityId,
                routeId,
                parseInt(newFromWaypointId),
                parseInt(newToWaypointId)
            );

            // Reset form and reload data
            setNewFromWaypointId('');
            setNewToWaypointId('');
            setShowAddSegment(false);
            await loadData();
        } catch (err) {
            console.error('Error adding segment:', err);
            setError(err.response?.data?.error || t('addSegmentFailed'));
        } finally {
            setIsAddingSegment(false);
        }
    };

    // Handle delete segment confirmation
    const handleDeleteClick = (segment) => {
        setDeletingSegment(segment);
        setShowDeleteConfirm(true);
    };

    // Handle delete segment
    const handleDeleteSegment = async () => {
        if (!deletingSegment) return;

        setShowDeleteConfirm(false);
        setError(null);

        try {
            await segmentMatrixService.deleteSegment(
                entityId,
                routeId,
                deletingSegment.fromWaypoint.id,
                deletingSegment.toWaypoint.id,
                deletingSegment.archivedType || 'representative',
                deletingSegment.variant ?? null,
                isPrivateRouteSegment(deletingSegment),
                deletingSegment.isSequential
            );

            setDeletingSegment(null);
            await loadData();
        } catch (err) {
            console.error('Error deleting segment:', err);
            setError(err.response?.data?.error || t('deleteSegmentFailed'));
        }
    };

    // Fetch ORS alternatives for a segment before showing selection/confirmation
    const handleSaveORSClick = async (segment) => {
        const segKey = buildSegmentKey(segment.fromWaypoint.id, segment.toWaypoint.id);
        setFetchingAlternatives(segKey);
        setOrsMessage(null);
        setError(null);

        try {
            const result = await segmentMatrixService.getORSAlternatives(
                entityId, routeId,
                segment.fromWaypoint.id, segment.toWaypoint.id,
                { routingPreference: orsRoutingPreference }
            );

            if (result.success && result.alternatives?.length > 0) {
                if (result.alternatives.length === 1) {
                    // Only one route — show simple confirmation (same as before)
                    setOrsConfirmSegment(segment);
                    setShowORSConfirm(true);
                } else {
                    // Multiple alternatives — show selection UI
                    setOrsAlternatives({ segment, alternatives: result.alternatives });
                    setSelectedAlternativeIndex(0);
                    setAlternativeVariantName('');
                }
            } else {
                setOrsMessage({
                    type: 'error',
                    text: t('orsNoAlternatives'),
                    key: segKey
                });
            }
        } catch (err) {
            console.error('Error fetching ORS alternatives:', err);
            setOrsMessage({
                type: 'error',
                text: err.response?.data?.error || t('orsAlternativesFailed'),
                key: segKey
            });
        } finally {
            setFetchingAlternatives(null);
        }
    };

    // Handle saving ORS geometry as reference data for a segment (simple confirm — single route)
    const handleSaveORSReference = async (segment) => {
        setShowORSConfirm(false);
        setOrsConfirmSegment(null);
        const segKey = buildSegmentKey(segment.fromWaypoint.id, segment.toWaypoint.id);
        setSavingORSSegmentKey(segKey);
        setOrsMessage(null);
        setError(null);

        try {
            const result = await segmentMatrixService.saveORSReference(
                entityId, routeId,
                segment.fromWaypoint.id, segment.toWaypoint.id,
                {
                    routingPreference: orsRoutingPreference,
                    archivedType: segment.archivedType || 'representative'
                }
            );

            if (result.success) {
                setOrsMessage({
                    type: 'success',
                    text: t('orsReferenceSaved', { count: result.pointCount }),
                    key: segKey
                });
                setSegments(prev => prev.map(s =>
                    s.fromWaypoint?.id === segment.fromWaypoint.id &&
                    s.toWaypoint?.id === segment.toWaypoint.id
                        ? { ...s, hasReferenceData: true }
                        : s
                ));
            }
        } catch (err) {
            console.error('Error saving ORS reference:', err);
            setOrsMessage({
                type: 'error',
                text: err.response?.data?.error || t('orsReferenceFailed'),
                key: segKey
            });
        } finally {
            setSavingORSSegmentKey(null);
        }
    };

    // Handle saving a selected ORS alternative (from alternatives selection UI)
    const handleSaveSelectedAlternative = async () => {
        if (!orsAlternatives) return;
        const { segment, alternatives } = orsAlternatives;
        const selected = alternatives[selectedAlternativeIndex];
        if (!selected) return;

        // Variant name is optional for index 0 (primary route), required for others
        const variant = alternativeVariantName.trim() || null;
        if (selectedAlternativeIndex > 0 && !variant) {
            setError(t('variantNameRequired'));
            return;
        }
        // Validate variant name format
        if (variant && !isValidVariantName(variant)) {
            setError(t('variantNameInvalid'));
            return;
        }

        const segKey = buildSegmentKey(segment.fromWaypoint.id, segment.toWaypoint.id);
        setSavingAlternative(true);
        setError(null);

        try {
            const result = await segmentMatrixService.saveORSReference(
                entityId, routeId,
                segment.fromWaypoint.id, segment.toWaypoint.id,
                {
                    archivedType: segment.archivedType || 'representative',
                    geometry: selected.geometry,
                    durationSeconds: selected.durationSeconds,
                    distanceMeters: selected.distanceMeters,
                    variant
                }
            );

            if (result.success) {
                setOrsMessage({
                    type: 'success',
                    text: variant
                        ? t('orsVariantSaved', { variant, count: result.pointCount })
                        : t('orsReferenceSaved', { count: result.pointCount }),
                    key: segKey
                });
                setSegments(prev => prev.map(s =>
                    s.fromWaypoint?.id === segment.fromWaypoint.id &&
                    s.toWaypoint?.id === segment.toWaypoint.id
                        ? { ...s, hasReferenceData: true }
                        : s
                ));
                setOrsAlternatives(null);
            }
        } catch (err) {
            console.error('Error saving ORS alternative:', err);
            setOrsMessage({
                type: 'error',
                text: err.response?.data?.error || t('orsReferenceFailed'),
                key: segKey
            });
        } finally {
            setSavingAlternative(false);
        }
    };

    // Handle saving ORS geometry as sequential reference for the entire route
    const handleSaveORSSequentialReference = async () => {
        setShowORSSequentialConfirm(false);
        setSavingORSSequential(true);
        setOrsSequentialMessage(null);
        setError(null);

        try {
            const result = await segmentMatrixService.saveORSSequentialReference(
                entityId,
                routeId,
                { routingPreference: orsRoutingPreference }
            );

            if (result.success) {
                setOrsSequentialMessage({
                    type: 'success',
                    text: t('orsSequentialSaved', { count: result.pointCount })
                });
                // Keep this status in sync for warning text context.
                setStatus(prev => ({ ...prev, hasRepresentativeReference: true }));
            }
        } catch (err) {
            console.error('Error saving ORS sequential reference:', err);
            setOrsSequentialMessage({
                type: 'error',
                text: err.response?.data?.error || t('orsSequentialFailed')
            });
        } finally {
            setSavingORSSequential(false);
        }
    };

    // Get waypoints that are not yet used as "from" in segments for potential additions
    const getAvailableFromWaypoints = () => waypoints;
    const getAvailableToWaypoints = () => waypoints.filter(w => w.id !== parseInt(newFromWaypointId));

    const renderProviderPolicyToggle = (segment) => {
        const segmentKey = getSegmentActionKey(segment);
        return (
            <label
                className={cn("flex items-center gap-1 text-xs", text.secondary)}
                title={t('privateRouteTooltip')}
            >
                <input
                    type="checkbox"
                    checked={isPrivateRouteSegment(segment)}
                    disabled={updatingPolicySegmentKey === segmentKey}
                    onChange={(event) => handleProviderPolicyToggle(segment, event.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="whitespace-nowrap">{t('privateRoute')}</span>
            </label>
        );
    };

    const renderWaypointDetectionModeSelect = (segment) => {
        const segmentKey = getSegmentActionKey(segment);
        const value = segment.waypointDetectionMode === 'manual' ? 'manual' : 'auto';
        return (
            <select
                value={value}
                disabled={updatingDetectionModeSegmentKey === segmentKey}
                onChange={(event) => handleWaypointDetectionModeChange(segment, event.target.value)}
                title={t('waypointDetectionModeTooltip')}
                aria-label={t('waypointDetectionMode')}
                className={cn(
                    "h-7 w-[4.75rem] max-w-full rounded border px-1 text-xs",
                    background.primary,
                    border.primary,
                    text.primary
                )}
            >
                <option value="auto">{t('waypointDetectionAuto', 'Auto')}</option>
                <option value="manual">{t('waypointDetectionManual', 'Manual')}</option>
            </select>
        );
    };

    const renderSegmentAttributesSelect = (segment) => {
        const segmentKey = getSegmentActionKey(segment);
        const value = segment.segmentAttributes === 'complex' ? 'complex' : 'simple';
        return (
            <label
                className={cn(
                    "flex h-7 w-[6rem] items-center gap-0.5 rounded border px-1",
                    background.primary,
                    border.primary,
                    text.primary
                )}
                title={t('segmentAttributesTooltip')}
            >
                <AlertTriangle className={cn("h-3.5 w-3.5", value === 'complex' ? 'text-amber-500' : text.muted)} />
                <select
                    value={value}
                    disabled={updatingAttributesSegmentKey === segmentKey}
                    onChange={(event) => handleSegmentAttributesChange(segment, event.target.value)}
                    title={t('segmentAttributesTooltip')}
                    aria-label={t('segmentAttributes')}
                    className={cn(
                        "w-[4.5rem] max-w-full bg-transparent text-xs outline-none",
                        text.primary
                    )}
                >
                    <option value="simple">{t('segmentAttributesSimple')}</option>
                    <option value="complex">{t('segmentAttributesComplex')}</option>
                </select>
            </label>
        );
    };

    const renderManualEditControls = (segment) => (
        <>
            <label
                className={cn(
                    "flex h-8 items-center gap-1 rounded border px-2",
                    background.primary,
                    border.primary,
                    text.primary
                )}
                title={t('segmentDurationMinutes', 'Duration minutes')}
            >
                <Clock className={cn("h-4 w-4", text.muted)} />
                <input
                    type="number"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    className={cn("w-12 bg-transparent text-sm outline-none", text.primary)}
                    aria-label={t('segmentDurationMinutes', 'Duration minutes')}
                    min="0"
                    step="0.1"
                    autoFocus
                />
                <span className={cn("text-xs", text.muted)}>{t('min', 'min')}</span>
            </label>
            <label
                className={cn(
                    "flex h-8 items-center gap-1 rounded border px-2",
                    background.primary,
                    border.primary,
                    text.primary
                )}
                title={`${t('segmentDistance', 'Distance')} (${distanceUnitLabel})`}
            >
                <Route className={cn("h-4 w-4", text.muted)} />
                <input
                    type="number"
                    value={editDistanceValue}
                    onChange={(e) => setEditDistanceValue(e.target.value)}
                    className={cn("w-14 bg-transparent text-sm outline-none", text.primary)}
                    aria-label={`${t('segmentDistance', 'Distance')} (${distanceUnitLabel})`}
                    min="0"
                    step="0.1"
                />
                <span className={cn("text-xs", text.muted)}>{distanceUnitLabel}</span>
            </label>
            <button
                onClick={() => handleSaveEdit(segment)}
                className="p-1 text-green-600 hover:text-green-700"
                title={t('save', 'Save')}
            >
                <Save className="w-4 h-4" />
            </button>
            <button
                onClick={() => {
                    setEditingSegment(null);
                    setEditValue('');
                    setEditDistanceValue('');
                }}
                className={cn("p-1", text.muted, "hover:text-red-600")}
                title={t('cancel', 'Cancel')}
            >
                <X className="w-4 h-4" />
            </button>
        </>
    );

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
                onClick={onClose}
            />

            {/* Modal */}
            <div className="flex min-h-full items-center justify-center p-4">
                <div className={cn(
                    "relative w-full max-w-3xl rounded-lg shadow-xl",
                    background.primary,
                    "transform transition-all"
                )}>
                    {/* Header */}
                    <div className={cn(
                        "flex items-center justify-between p-4 border-b",
                        border.primary
                    )}>
                        <div className="flex items-center gap-2">
                            <Calculator className={cn("w-5 h-5", text.primary)} />
                            <h2 className={cn("text-lg font-semibold", text.primary)}>
                                {t('travelTimes')}
                            </h2>
                        </div>
                        <button
                            onClick={onClose}
                            className={cn(
                                "p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700",
                                text.secondary
                            )}
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Content */}
                    <div className="p-4 max-h-[60vh] overflow-y-auto">
                        {/* Route info */}
                        <div className={cn("mb-4 p-3 rounded-lg", background.secondary)}>
                            <p className={cn("font-medium", text.primary)}>{routeName}</p>
                            <p className={cn("text-sm", text.muted)}>
                                {waypoints.length} {t('waypoints')}
                            </p>
                        </div>

                        {/* Status section */}
                        {status && (
                            <div className={cn("mb-4 p-3 rounded-lg border", border.primary)}>
                                <div className="flex items-center justify-between mb-2">
                                    <span className={cn("text-sm font-medium", text.primary)}>
                                        {t('calculationStatus')}
                                    </span>
                                    {status.needsRecalculation ? (
                                        <span className="flex items-center gap-1 text-amber-600 dark:text-amber-400 text-sm">
                                            <AlertTriangle className="w-4 h-4" />
                                            {t('needsRecalculation')}
                                        </span>
                                    ) : status.hasCalculatedTimes ? (
                                        <span className="flex items-center gap-1 text-green-600 dark:text-green-400 text-sm">
                                            <Check className="w-4 h-4" />
                                            {t('upToDate')}
                                        </span>
                                    ) : (
                                        <span className={cn("text-sm", text.muted)}>
                                            {t('notCalculated')}
                                        </span>
                                    )}
                                </div>

                                {status.lastCalculatedAt && (
                                    <p className={cn("text-xs", text.muted)}>
                                        {t('lastCalculated')}: {formatDateTime(status.lastCalculatedAt)}
                                    </p>
                                )}

                                {(status.totalDurationMinutes > 0 || status.totalDistanceKm > 0) && (
                                    <p className={cn("text-sm mt-1", text.secondary)}>
                                        {t('totalRouteDuration')}: {formatDurationMinutes(status.totalDurationMinutes, t)}
                                        {status.totalDistanceKm > 0 && ` • ${t('totalRouteDistance')}: ${formatSegmentDistanceForDisplay(status.totalDistanceKm)}`}
                                    </p>
                                )}
                            </div>
                        )}

                        {/* Error message */}
                        {error && (
                            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                                <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
                            </div>
                        )}

                        {/* Calculate button */}
                        <div className="mb-4">
                            <ThemedButton
                                onClick={handleCalculateClick}
                                disabled={isCalculating || isLoading}
                                icon={Calculator}
                                className="w-full"
                            >
                                {isCalculating ? t('calculating') : t('calculateTravelTimes')}
                            </ThemedButton>
                            <p className={cn("text-xs mt-1", text.muted)}>
                                {t('calculateTravelTimesHelp')}
                            </p>
                        </div>

                        {/* ORS routing preference toggle */}
                        <div className={cn("mb-4 p-3 rounded-lg border", border.primary)}>
                            <div className="flex flex-wrap items-center justify-between gap-2">
                                <span className={cn("text-sm font-medium", text.primary)}>
                                    {t('orsRoutingMode')}
                                </span>
                                <div className={cn("inline-flex rounded border overflow-hidden", border.primary)}>
                                    <button
                                        type="button"
                                        onClick={() => setOrsRoutingPreference('recommended')}
                                        className={cn(
                                            "px-3 py-1 text-xs",
                                            orsRoutingPreference === 'recommended'
                                                ? "bg-blue-600 text-white"
                                                : cn(background.primary, text.secondary, "hover:bg-gray-100 dark:hover:bg-gray-700")
                                        )}
                                    >
                                        {t('orsRoutingRecommended')}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setOrsRoutingPreference('shortest')}
                                        className={cn(
                                            "px-3 py-1 text-xs border-l",
                                            border.primary,
                                            orsRoutingPreference === 'shortest'
                                                ? "bg-blue-600 text-white"
                                                : cn(background.primary, text.secondary, "hover:bg-gray-100 dark:hover:bg-gray-700")
                                        )}
                                    >
                                        {t('orsRoutingShortest')}
                                    </button>
                                </div>
                            </div>
                            <p className={cn("text-xs mt-2", text.muted)}>
                                {t('orsRoutingModeHelp')}
                            </p>
                        </div>

                        {/* Segments list */}
                        {isLoading ? (
                            <div className="flex items-center justify-center py-8">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                            </div>
                        ) : segments.length > 0 ? (
                            <div className="space-y-4">
                                {/* Sequential segments */}
                                <div className="space-y-2">
                                    <div className="flex items-center gap-2 mb-2">
                                        <h3 className={cn("text-sm font-medium", text.primary)}>
                                            {t('sequentialSegments')}
                                        </h3>
                                        {/* Section-level ORS save button for sequential reference */}
                                        <button
                                            onClick={() => setShowORSSequentialConfirm(true)}
                                            disabled={savingORSSequential}
                                            className={cn(
                                                "flex items-center gap-1 px-2 py-1 rounded text-xs",
                                                "hover:text-green-600",
                                                text.muted
                                            )}
                                            title={t('saveORSSequentialReference')}
                                        >
                                            {savingORSSequential ? (
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                            ) : (
                                                <Route className="w-4 h-4" />
                                            )}
                                            <span>{t('saveORSSequentialReference')}</span>
                                        </button>
                                    </div>
                                    {/* Sequential ORS feedback message */}
                                    {orsSequentialMessage && (
                                        <p className={cn("text-xs mb-1", {
                                            'text-green-600': orsSequentialMessage.type === 'success',
                                            'text-amber-600': orsSequentialMessage.type === 'skip',
                                            'text-red-600': orsSequentialMessage.type === 'error'
                                        })}>
                                            {orsSequentialMessage.text}
                                        </p>
                                    )}
                                    {segments.filter(s => s.isSequential).map((segment, index) => (
                                        <div
                                            key={segment.id || `seq-${index}`}
                                            className={cn(
                                                "flex flex-col gap-3 p-3 rounded-lg md:flex-row md:items-center md:justify-between",
                                                background.secondary
                                            )}
                                        >
                                            <div className="flex items-center gap-2 flex-1 min-w-0">
                                                <MapPin className={cn("w-4 h-4 flex-shrink-0", text.muted)} />
                                                <div className="min-w-0">
                                                    <p
                                                        className={cn("text-sm font-medium truncate", text.primary)}
                                                        title={getSegmentLabel(segment.fromWaypoint, segment.toWaypoint)}
                                                    >
                                                        {getSegmentLabel(segment.fromWaypoint, segment.toWaypoint)}
                                                    </p>
                                                    <div className={cn("flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs", text.muted)}>
                                                        <span>{segment.archivedType || 'representative'}</span>
                                                        {segment.variant && (
                                                            <span>{t('segmentMatrixVariantBadge', { name: segment.variant })}</span>
                                                        )}
                                                        {segment.distanceKm != null && (
                                                            <span>{formatSegmentDistanceForDisplay(segment.distanceKm)}</span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="flex flex-wrap items-center justify-end gap-1.5">
                                                {renderProviderPolicyToggle(segment)}
                                                {renderWaypointDetectionModeSelect(segment)}
                                                {renderSegmentAttributesSelect(segment)}
                                                {editingSegment?.id === segment.id ? (
                                                    renderManualEditControls(segment)
                                                ) : (
                                                    <>
                                                        <div className="flex items-center gap-1">
                                                            <Clock className={cn("w-4 h-4", text.muted)} />
                                                            <span className={cn("text-sm font-medium", text.primary)}>
                                                                {formatDurationMinutes(segment.durationMinutes, t)}
                                                            </span>
                                                        </div>
                                                        <button
                                                            onClick={() => {
                                                                setEditingSegment(segment);
                                                                setEditValue(segment.durationMinutes?.toString() || '');
                                                                setEditDistanceValue(distanceKmToDisplayValue(segment.distanceKm)?.toString() || '');
                                                            }}
                                                            className={cn("p-1", text.muted, "hover:text-blue-600")}
                                                            title={t('editManually')}
                                                        >
                                                            <Edit2 className="w-4 h-4" />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteClick(segment)}
                                                            className={cn("p-1", text.muted, "hover:text-red-600")}
                                                            title={t('deleteSegment')}
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* Reorder segments (bidirectional for flexible routing) */}
                                {segments.filter(s => !s.isSequential).length > 0 && (
                                    <div className="space-y-2">
                                        <h3 className={cn("text-sm font-medium mb-2", text.primary)}>
                                            {t('reorderSegments')}
                                        </h3>
                                        <p className={cn("text-xs mb-2", text.muted)}>
                                            {t('reorderSegmentsHelp')}
                                        </p>
                                        {segments.filter(s => !s.isSequential).map((segment, index) => (
                                            <div
                                                key={segment.id || `shortcut-${index}`}
                                                className={cn(
                                                    "flex flex-col gap-3 p-3 rounded-lg border-l-4 border-amber-500 md:flex-row md:items-center md:justify-between",
                                                    background.secondary
                                                )}
                                            >
                                                <div className="flex items-center gap-2 flex-1 min-w-0">
                                                    <MapPin className={cn("w-4 h-4 flex-shrink-0 text-amber-500")} />
                                                    <div className="min-w-0">
                                                        <p
                                                            className={cn("text-sm font-medium truncate", text.primary)}
                                                            title={getSegmentLabel(segment.fromWaypoint, segment.toWaypoint)}
                                                        >
                                                            {getSegmentLabel(segment.fromWaypoint, segment.toWaypoint)}
                                                        </p>
                                                        <div className={cn("flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs", text.muted)}>
                                                            <span>{segment.archivedType || 'representative'}</span>
                                                            {segment.variant && (
                                                                <span>{t('segmentMatrixVariantBadge', { name: segment.variant })}</span>
                                                            )}
                                                            {segment.distanceKm != null && (
                                                                <span>{formatSegmentDistanceForDisplay(segment.distanceKm)}</span>
                                                            )}
                                                            {segment.hasReferenceData && (
                                                                <span className="flex items-center gap-1 text-green-600 dark:text-green-400">
                                                                    <Check className="w-3 h-3" />
                                                                    {t('hasReferenceData')}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="flex flex-wrap items-center justify-end gap-1.5">
                                                    {renderProviderPolicyToggle(segment)}
                                                    {renderWaypointDetectionModeSelect(segment)}
                                                    {renderSegmentAttributesSelect(segment)}
                                                    {editingSegment?.id === segment.id ? (
                                                        renderManualEditControls(segment)
                                                    ) : (
                                                        <>
                                                            <div className="flex items-center gap-1">
                                                                <Clock className={cn("w-4 h-4", text.muted)} />
                                                                <span className={cn("text-sm font-medium", text.primary)}>
                                                                    {formatDurationMinutes(segment.durationMinutes, t)}
                                                                </span>
                                                            </div>
                                                            <button
                                                                onClick={() => {
                                                                    setEditingSegment(segment);
                                                                    setEditValue(segment.durationMinutes?.toString() || '');
                                                                    setEditDistanceValue(distanceKmToDisplayValue(segment.distanceKm)?.toString() || '');
                                                                }}
                                                                className={cn("p-1", text.muted, "hover:text-blue-600")}
                                                                title={t('editManually')}
                                                            >
                                                                <Edit2 className="w-4 h-4" />
                                                            </button>
                                                            <button
                                                                onClick={() => handleDeleteClick(segment)}
                                                                className={cn("p-1", text.muted, "hover:text-red-600")}
                                                                title={t('deleteSegment')}
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </button>
                                                            <button
                                                                onClick={() => handleSaveORSClick(segment)}
                                                                disabled={
                                                                    savingORSSegmentKey === buildSegmentKey(segment.fromWaypoint.id, segment.toWaypoint.id) ||
                                                                    fetchingAlternatives === buildSegmentKey(segment.fromWaypoint.id, segment.toWaypoint.id)
                                                                }
                                                                className={cn(
                                                                    "p-1",
                                                                    segment.hasReferenceData ? "text-green-600" : text.muted,
                                                                    "hover:text-green-600"
                                                                )}
                                                                title={t('saveORSReference')}
                                                            >
                                                                {(savingORSSegmentKey === buildSegmentKey(segment.fromWaypoint.id, segment.toWaypoint.id) ||
                                                                  fetchingAlternatives === buildSegmentKey(segment.fromWaypoint.id, segment.toWaypoint.id)) ? (
                                                                    <Loader2 className="w-4 h-4 animate-spin" />
                                                                ) : (
                                                                    <Route className="w-4 h-4" />
                                                                )}
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                                {/* ORS reference feedback message for this segment */}
                                                {orsMessage && orsMessage.key === buildSegmentKey(segment.fromWaypoint.id, segment.toWaypoint.id) && (
                                                    <p className={cn("text-xs mt-1", {
                                                        'text-green-600': orsMessage.type === 'success',
                                                        'text-amber-600': orsMessage.type === 'skip',
                                                        'text-red-600': orsMessage.type === 'error'
                                                    })}>
                                                        {orsMessage.text}
                                                    </p>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* Add new segment section */}
                                <div className={cn("mt-4 p-3 rounded-lg border", border.primary)}>
                                    {!showAddSegment ? (
                                        <button
                                            onClick={() => setShowAddSegment(true)}
                                            className={cn(
                                                "flex items-center gap-2 w-full text-left text-sm",
                                                text.primary,
                                                "hover:text-blue-600"
                                            )}
                                        >
                                            <Plus className="w-4 h-4" />
                                            {t('addNewSegment')}
                                        </button>
                                    ) : (
                                        <div className="space-y-3">
                                            <div className="flex items-center justify-between">
                                                <h4 className={cn("text-sm font-medium", text.primary)}>
                                                    {t('addNewSegment')}
                                                </h4>
                                                <button
                                                    onClick={() => {
                                                        setShowAddSegment(false);
                                                        setNewFromWaypointId('');
                                                        setNewToWaypointId('');
                                                    }}
                                                    className={cn("p-1", text.muted, "hover:text-red-600")}
                                                >
                                                    <X className="w-4 h-4" />
                                                </button>
                                            </div>
                                            <div className="grid grid-cols-2 gap-2">
                                                <div>
                                                    <label className={cn("text-xs", text.muted)}>{t('fromWaypoint')}</label>
                                                    <select
                                                        value={newFromWaypointId}
                                                        onChange={(e) => setNewFromWaypointId(e.target.value)}
                                                        className={cn(
                                                            "w-full mt-1 px-2 py-1.5 text-sm rounded border",
                                                            background.primary,
                                                            border.primary,
                                                            text.primary
                                                        )}
                                                    >
                                                        <option value="">{t('selectWaypoint')}</option>
                                                        {getAvailableFromWaypoints().map(wp => (
                                                            <option key={wp.id} value={wp.id}>{getWaypointName(wp)}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div>
                                                    <label className={cn("text-xs", text.muted)}>{t('toWaypoint')}</label>
                                                    <select
                                                        value={newToWaypointId}
                                                        onChange={(e) => setNewToWaypointId(e.target.value)}
                                                        className={cn(
                                                            "w-full mt-1 px-2 py-1.5 text-sm rounded border",
                                                            background.primary,
                                                            border.primary,
                                                            text.primary
                                                        )}
                                                        disabled={!newFromWaypointId}
                                                    >
                                                        <option value="">{t('selectWaypoint')}</option>
                                                        {getAvailableToWaypoints().map(wp => (
                                                            <option key={wp.id} value={wp.id}>{getWaypointName(wp)}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>
                                            {/* Inline error for add-segment form */}
                                            {error && (
                                                <p className="text-red-600 dark:text-red-400 text-xs">{error}</p>
                                            )}
                                            <ThemedButton
                                                onClick={handleAddSegment}
                                                disabled={isAddingSegment || !newFromWaypointId || !newToWaypointId}
                                                icon={Calculator}
                                                size="sm"
                                                className="w-full"
                                            >
                                                {isAddingSegment ? t('calculating') : t('calculateAndAdd')}
                                            </ThemedButton>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className={cn("text-center py-8", text.muted)}>
                                <Clock className="w-12 h-12 mx-auto mb-2 opacity-50" />
                                <p>{t('noTravelTimesYet')}</p>
                                <p className="text-sm mt-1">{t('clickCalculateToStart')}</p>
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className={cn(
                        "flex items-center justify-between p-4 border-t",
                        border.primary
                    )}>
                        <p className={cn("text-xs", text.muted)}>
                            © <a href="https://openrouteservice.org" target="_blank" rel="noopener noreferrer" className="underline hover:text-blue-500">openrouteservice.org</a> by HeiGIT | Map data © OpenStreetMap contributors
                        </p>
                        <ThemedButton variant="secondary" onClick={onClose}>
                            {t('close')}
                        </ThemedButton>
                    </div>
                </div>
            </div>

            {/* ORS Alternatives Selection Dialog */}
            {orsAlternatives && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
                    <div className="fixed inset-0 bg-black/50" onClick={() => setOrsAlternatives(null)} />
                    <div className={cn(
                        "relative w-full max-w-lg rounded-xl shadow-2xl border",
                        background.primary, border.primary
                    )}>
                        {/* Header */}
                        <div className={cn("flex items-center justify-between p-4 border-b", border.primary)}>
                            <h3 className={cn("text-lg font-semibold", text.primary)}>
                                {t('orsSelectAlternative')}
                            </h3>
                            <button
                                onClick={() => setOrsAlternatives(null)}
                                className={cn("p-1 rounded-lg", text.muted, "hover:text-red-600")}
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Segment info */}
                        <div className={cn("px-4 pt-3 text-sm", text.muted)}>
                            {getWaypointName(orsAlternatives.segment.fromWaypoint)} → {getWaypointName(orsAlternatives.segment.toWaypoint)}
                        </div>

                        {/* Alternatives list */}
                        <div className="p-4 space-y-2 max-h-64 overflow-y-auto">
                            {orsAlternatives.alternatives.map((alt, idx) => {
                                // Color coding for alternatives
                                const colors = ['text-blue-600', 'text-green-600', 'text-orange-500', 'text-purple-600', 'text-red-500'];
                                const bgColors = ['bg-blue-50 dark:bg-blue-950', 'bg-green-50 dark:bg-green-950', 'bg-orange-50 dark:bg-orange-950', 'bg-purple-50 dark:bg-purple-950', 'bg-red-50 dark:bg-red-950'];
                                const borderColors = ['border-blue-300 dark:border-blue-700', 'border-green-300 dark:border-green-700', 'border-orange-300 dark:border-orange-700', 'border-purple-300 dark:border-purple-700', 'border-red-300 dark:border-red-700'];
                                const colorClass = colors[idx % colors.length];
                                const bgClass = bgColors[idx % bgColors.length];
                                const borderClass = borderColors[idx % borderColors.length];

                                const distanceKm = Number.isFinite(Number(alt.distanceMeters))
                                    ? Number(alt.distanceMeters) / 1000
                                    : null;
                                const distanceLabel = distanceKm !== null
                                    ? formatSegmentDistanceForDisplay(distanceKm)
                                    : '-';
                                const durMin = alt.durationSeconds ? Math.round(alt.durationSeconds / 60) : '-';

                                return (
                                    <label
                                        key={idx}
                                        className={cn(
                                            "flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors",
                                            selectedAlternativeIndex === idx
                                                ? `${bgClass} ${borderClass}`
                                                : `${background.secondary} ${border.primary} hover:border-blue-300`
                                        )}
                                    >
                                        <input
                                            type="radio"
                                            name="orsAlternative"
                                            checked={selectedAlternativeIndex === idx}
                                            onChange={() => setSelectedAlternativeIndex(idx)}
                                            className="w-4 h-4"
                                        />
                                        <div className={cn("w-3 h-3 rounded-full flex-shrink-0", colorClass.replace('text-', 'bg-'))} />
                                        <div className="flex-1 min-w-0">
                                            <p className={cn("text-sm font-medium", text.primary)}>
                                                {t('orsRouteOption', { number: idx + 1 })}
                                            </p>
                                            <p className={cn("text-xs", text.muted)}>
                                                {distanceLabel} · {durMin} {t('min')}
                                            </p>
                                        </div>
                                    </label>
                                );
                            })}
                        </div>

                        {/* Variant name input */}
                        <div className={cn("px-4 pb-3")}>
                            <label className={cn("block text-xs mb-1", text.muted)}>
                                {t('orsVariantName')} {selectedAlternativeIndex > 0 && <span className="text-red-500">*</span>}
                            </label>
                            <input
                                type="text"
                                value={alternativeVariantName}
                                onChange={(e) => setAlternativeVariantName(sanitizeVariantName(e.target.value))}
                                placeholder={selectedAlternativeIndex === 0 ? t('orsVariantNameOptional') : t('orsVariantNamePlaceholder')}
                                className={cn(
                                    "w-full px-3 py-2 text-sm rounded-lg border",
                                    background.primary, border.primary, text.primary
                                )}
                                maxLength={30}
                            />
                            <p className={cn("text-xs mt-1", text.muted)}>
                                {t('orsVariantNameHelp')}
                            </p>
                        </div>

                        {/* Footer actions */}
                        <div className={cn("flex items-center justify-end gap-2 p-4 border-t", border.primary)}>
                            <ThemedButton
                                variant="secondary"
                                onClick={() => setOrsAlternatives(null)}
                                size="sm"
                            >
                                {t('cancel')}
                            </ThemedButton>
                            <ThemedButton
                                onClick={handleSaveSelectedAlternative}
                                disabled={savingAlternative || (selectedAlternativeIndex > 0 && !alternativeVariantName.trim())}
                                icon={savingAlternative ? Loader2 : Save}
                                size="sm"
                            >
                                {savingAlternative ? t('saving') : t('orsSaveSelectedRoute')}
                            </ThemedButton>
                        </div>
                    </div>
                </div>
            )}

            {/* Confirmation Dialog */}
            <ConfirmDialogShell
                isOpen={showConfirmDialog}
                onClose={() => setShowConfirmDialog(false)}
                onConfirm={handleCalculate}
                title={t('confirmCalculation')}
                confirmLabel={t('calculate')}
                confirmIcon={Calculator}
                confirmVariant="warning"
                showIcon={true}
                iconVariant="warning"
            >
                <div className={cn("text-center", text.secondary)}>
                    <p className="mb-2">{t('confirmCalculationMessage')}</p>
                    <p className={cn("text-sm", text.muted)}>{t('confirmCalculationWarning')}</p>
                </div>
            </ConfirmDialogShell>

            {/* Delete Confirmation Dialog */}
            <ConfirmDialogShell
                isOpen={showDeleteConfirm}
                onClose={() => {
                    setShowDeleteConfirm(false);
                    setDeletingSegment(null);
                }}
                onConfirm={handleDeleteSegment}
                title={t('confirmDeleteSegment')}
                confirmLabel={t('delete')}
                confirmIcon={Trash2}
                confirmVariant="danger"
                showIcon={true}
                iconVariant="danger"
            >
                <div className={cn("text-center", text.secondary)}>
                    {deletingSegment && (
                        <>
                            <p className="mb-2">
                                {t('confirmDeleteSegmentMessage', {
                                    from: getWaypointName(deletingSegment.fromWaypoint),
                                    to: getWaypointName(deletingSegment.toWaypoint)
                                })}
                            </p>
                            <p className={cn("text-sm", text.muted)}>
                                {t('deleteSegmentWarning')}
                            </p>
                            <p className={cn("text-sm mt-2", text.muted)}>
                                {t('segmentMatrixDeleteVariantConfirm', {
                                    name: deletingSegment.variant || 'standard',
                                    from: getWaypointName(deletingSegment.fromWaypoint),
                                    to: getWaypointName(deletingSegment.toWaypoint)
                                })}
                            </p>
                            {deletingSegment.hasReferenceData && (
                                <p className={cn("text-sm mt-2 text-amber-600 dark:text-amber-400")}>
                                    {t('deleteSegmentReferenceWarning')}
                                </p>
                            )}
                            {isPrivateRouteSegment(deletingSegment) && (
                                <p className={cn("text-sm mt-2 text-red-600 dark:text-red-400")}>
                                    {t('deletePrivateRoutePolicyWarning')}
                                </p>
                            )}
                        </>
                    )}
                </div>
            </ConfirmDialogShell>

            {/* Save ORS Reference Confirmation Dialog */}
            <ConfirmDialogShell
                isOpen={showORSConfirm}
                onClose={() => {
                    setShowORSConfirm(false);
                    setOrsConfirmSegment(null);
                }}
                onConfirm={() => handleSaveORSReference(orsConfirmSegment)}
                title={t('confirmSaveORSReference')}
                confirmLabel={t('save')}
                confirmIcon={Route}
                confirmVariant="primary"
                showIcon={true}
                iconVariant="info"
            >
                <div className={cn("text-center", text.secondary)}>
                    {orsConfirmSegment && (
                        <p className="mb-2">
                            {t('confirmSaveORSReferenceMessage', {
                                from: getWaypointName(orsConfirmSegment.fromWaypoint),
                                to: getWaypointName(orsConfirmSegment.toWaypoint)
                            })}
                        </p>
                    )}
                    {orsConfirmSegment?.hasReferenceData && (
                        <p className={cn("text-sm mb-2 text-amber-600 dark:text-amber-400")}>
                            {t('orsReplaceSegmentWarning')}
                        </p>
                    )}
                    <p className={cn("text-sm", text.muted)}>
                        {t('orsRoutingModeSelected', {
                            mode: orsRoutingPreference === 'shortest'
                                ? t('orsRoutingShortest')
                                : t('orsRoutingRecommended')
                        })}
                    </p>
                </div>
            </ConfirmDialogShell>

            {/* Save ORS Sequential Reference Confirmation Dialog */}
            <ConfirmDialogShell
                isOpen={showORSSequentialConfirm}
                onClose={() => setShowORSSequentialConfirm(false)}
                onConfirm={handleSaveORSSequentialReference}
                title={t('saveORSSequentialReference')}
                confirmLabel={t('save')}
                confirmIcon={Route}
                confirmVariant="primary"
                showIcon={true}
                iconVariant="info"
            >
                <div className={cn("text-center", text.secondary)}>
                    <p className="mb-2">
                        {t('confirmSaveORSSequentialMessage')}
                    </p>
                    {status?.hasRepresentativeReference && (
                        <p className={cn("text-sm mb-2 text-amber-600 dark:text-amber-400")}>
                            {t('orsReplaceSequentialWarning')}
                        </p>
                    )}
                    <p className={cn("text-sm", text.muted)}>
                        {t('orsRoutingModeSelected', {
                            mode: orsRoutingPreference === 'shortest'
                                ? t('orsRoutingShortest')
                                : t('orsRoutingRecommended')
                        })}
                    </p>
                </div>
            </ConfirmDialogShell>
        </div>
    );
};

export default SegmentMatrixModal;
